import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuCheck,
  LuChevronLeft,
  LuDownload,
  LuFileText,
  LuGraduationCap,
  LuBriefcase,
  LuListChecks,
  LuInfo,
} from "react-icons/lu";
import { Btn, Sheet, Spinner, Note, MatchRing } from "../components/Ui";
import ResumeDoc from "../components/ResumeDoc";
import "./customResume.css";

/* ---------------------------------------------------------------
   Custom resume — three steps: see the gap, choose what to apply,
   review the rewritten document before it leaves the app.

   Everything here runs on services that already exist:
     resume-update-suggestions-all-v4  (streamed suggestions, and its
       `suggestions` branch which merges them into a new resume_json)
     create_pdf_from_resume_suggestions_v2  (the downloadable files)
   --------------------------------------------------------------- */

const MERGE_API = "https://resume-update-suggestions-all-v4-980069659423.us-east1.run.app";
const CREATE_PDF_API =
  "https://us-east1-foursssolutions.cloudfunctions.net/create_pdf_from_resume_suggestions_v2";

const STEPS = ["See the gap", "Choose changes", "Review resume"];

function fileStamp() {
  const now = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(now.getMonth() + 1)}-${p(now.getDate())}-${now.getFullYear()}_${p(now.getHours())}-${p(
    now.getMinutes()
  )}`;
}

function downloadBase64(base64, mime, filename) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* A row of the step-1 comparison. `ok` drives the icon, never colour alone. */
function DiffRow({ label, ok, jobSide, youSide }) {
  return (
    <div className="cr-row">
      <div className="cr-row-label">
        <span className={`cr-dot${ok ? " ok" : ""}`}>{ok ? <LuCheck size={11} /> : "!"}</span>
        {label}
      </div>
      <div className="cr-row-side">{jobSide}</div>
      <div className="cr-row-side">{youSide}</div>
    </div>
  );
}

export default function CustomResumeSheet({ open, onClose, job, updates, resumeJson }) {
  const userEmail = useSelector((s) => s.auth.auth.email);
  const profileData = useSelector((s) => s.profile.profileData);

  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState({});
  const [skillPicks, setSkillPicks] = useState([]);
  const [merging, setMerging] = useState(false);
  const [after, setAfter] = useState(null);
  const [files, setFiles] = useState(null);
  const [downloading, setDownloading] = useState("");

  /* one memo so the derived lists keep stable identities across renders */
  const { assessment, certs, domain, experience, missingSkills } = useMemo(() => {
    const at = (t) => (updates || []).find((c) => c.chunk_type === t)?.data;
    const sg = at("skill_gaps") || {};
    return {
      assessment: at("assessment") || {},
      certs: at("certifications") || {},
      domain: at("domain_match") || {},
      experience: at("experience_enhancement") || [],
      missingSkills: [...(sg.critical_missing || []), ...(sg.recommended_missing || [])],
    };
  }, [updates]);

  /* Only offer sections the analysis actually returned something for —
     an empty checkbox that does nothing is worse than no checkbox. */
  const sections = useMemo(
    () =>
      [
        missingSkills.length && {
          key: "skill_gaps",
          icon: <LuListChecks size={15} />,
          label: "Skills",
          hint: `${missingSkills.length} requirement${missingSkills.length === 1 ? "" : "s"} your resume doesn't address`,
        },
        experience.length && {
          key: "experience_enhancement",
          icon: <LuBriefcase size={15} />,
          label: "Work experience",
          hint: `Stronger bullets for ${experience.length} role${experience.length === 1 ? "" : "s"}`,
        },
        (certs.missing || []).length && {
          key: "certifications",
          icon: <LuGraduationCap size={15} />,
          label: "Certifications",
          hint: `${certs.missing.length} the posting mentions`,
        },
      ].filter(Boolean),
    [missingSkills, experience, certs]
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setAfter(null);
    setFiles(null);
    setPicked(Object.fromEntries(sections.map((s) => [s.key, true])));
    setSkillPicks(missingSkills);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.job_id]);

  /* Only the chunks the user kept are sent to the merge, and the skills
     chunk is narrowed to the keywords they actually ticked. */
  const selectedSuggestions = useMemo(() => {
    return (updates || [])
      .filter((c) => {
        if (c.chunk_type === "assessment" || c.chunk_type === "domain_match") return true;
        if (c.chunk_type === "action_items") return false;
        return picked[c.chunk_type];
      })
      .map((c) => {
        if (c.chunk_type !== "skill_gaps") return c;
        const keep = (arr) => (arr || []).filter((s) => skillPicks.includes(s));
        return {
          ...c,
          data: {
            ...c.data,
            critical_missing: keep(c.data.critical_missing),
            recommended_missing: keep(c.data.recommended_missing),
          },
        };
      });
  }, [updates, picked, skillPicks]);

  const nothingPicked = !Object.values(picked).some(Boolean);

  const runMerge = async () => {
    setMerging(true);
    setStep(2);
    try {
      const res = await fetch(MERGE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id: userEmail,
          suggestions: { result: { data: selectedSuggestions } },
        }),
      });
      if (!res.ok) throw new Error(`Merge failed (${res.status})`);
      const data = await res.json();
      if (!data.resume_json) throw new Error("No resume returned");
      setAfter(data.resume_json);
    } catch (err) {
      toast.error("Could not build your tailored resume. Please try again.");
      setStep(1);
    } finally {
      setMerging(false);
    }
  };

  const fileBase = () => {
    const who =
      [profileData?.first_name, profileData?.last_name].filter(Boolean).join("_") ||
      (userEmail ? userEmail.split("@")[0] : "") ||
      "CareerSavvy";
    const jobPart = job?.job_title
      ? "_" + String(job.job_title).replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40)
      : "";
    return `${who}${jobPart}_Resume_${fileStamp()}`;
  };

  /* Primary PDF path: render ONLY the resume preview into a clean isolated
     window and print that. Printing it in-place fought the scrollable modal and
     an `position:absolute` print rule, which piled a multi-page resume onto one
     spot (overlapping text). A fresh document lets it flow across real pages.
     Instant, crisp selectable text, ATS-clean, and matches what you see. */
  const printResume = () => {
    const el = document.querySelector(".rd");
    const title = fileBase();
    const inPlace = () => {
      const prev = document.title;
      document.title = title;
      window.print();
      setTimeout(() => {
        document.title = prev;
      }, 1500);
    };
    if (!el) return inPlace();
    const w = window.open("", "_blank");
    if (!w) return inPlace(); // pop-up blocked → fall back to in-place print

    let head = "";
    document.querySelectorAll("style").forEach((s) => {
      head += `<style>${s.textContent}</style>`;
    });
    document.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
      if (l.href) head += `<link rel="stylesheet" href="${l.href}">`;
    });
    /* override any inherited print rule that positioned .rd absolutely */
    const fix =
      "@page{size:Letter;margin:0}html,body{margin:0;padding:0;background:#fff}" +
      "@media print{.csx .rd{position:static !important;top:auto !important;left:auto !important;" +
      "width:100% !important;max-width:none !important;margin:0 !important;padding:0.5in !important;" +
      "box-shadow:none !important;border-radius:0 !important;visibility:visible !important}}" +
      ".csx .rd{box-shadow:none !important;border-radius:0 !important;max-width:none !important;margin:0 auto}";

    w.document.open();
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>${head}` +
        `<style>${fix}</style></head><body class="csx"><div class="csx">${el.outerHTML}</div></body></html>`
    );
    w.document.close();

    const go = () => {
      try {
        w.focus();
        w.print();
      } catch (e) {
        /* noop */
      }
    };
    if (w.document.readyState === "complete") setTimeout(go, 500);
    else w.onload = () => setTimeout(go, 500);
  };

  const download = async (kind) => {
    setDownloading(kind);
    try {
      let f = files;
      if (!f) {
        /* The Word doc is built by a chain of two cold-start functions
           (create_pdf → resume_suggestions_file_generation_all) plus
           reportlab/python-docx work. Warm it returns in ~10s, but a cold
           first click can take a while — a 60s cap was aborting it, so the
           button looked like it "did nothing". Give it room, and tell the
           user it's working so the wait reads as progress, not a dead click. */
        if (kind === "docx") {
          toast.info("Preparing your Word document… this can take up to a minute on the first try.");
        }
        const controller = new AbortController();
        const killer = setTimeout(() => controller.abort(), 150000);
        let res;
        try {
          res = await fetch(CREATE_PDF_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email_id: userEmail,
              suggestions: { result: { data: selectedSuggestions } },
            }),
            signal: controller.signal,
          });
        } finally {
          clearTimeout(killer);
        }
        if (!res.ok) throw new Error(`File generation failed (${res.status})`);
        f = await res.json().catch(() => null);
        if (!f) throw new Error("File generation returned nothing");
        setFiles(f);
      }
      const wanted = kind === "pdf" ? f.base64_pdf : f.base64_docx;
      /* atob(undefined) throws an opaque InvalidCharacterError, which is what
         the generic "could not generate" toast was actually reporting */
      if (!wanted) {
        throw new Error(
          `The ${kind.toUpperCase()} came back empty. ${
            kind === "pdf" && f.base64_docx ? "Try DOCX instead." : "Please try again."
          }`
        );
      }
      const base = fileBase();
      if (kind === "pdf") downloadBase64(wanted, "application/pdf", `${base}.pdf`);
      else
        downloadBase64(
          wanted,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          `${base}.docx`
        );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[resume download]", err);
      toast.error(
        err.name === "AbortError"
          ? "The file service is taking too long. Use “Print / Save as PDF” for an instant copy, or try again."
          : err.message || "Could not generate the file. Please try again."
      );
    } finally {
      setDownloading("");
    }
  };

  const score = Number(assessment.match_percentage) || 0;

  const footer =
    step === 0 ? (
      <Btn full className="btn-pill btn-mint" onClick={() => setStep(1)} disabled={!sections.length}>
        <LuFileText size={15} /> {sections.length ? "Improve my resume for this job" : "Nothing to improve"}
      </Btn>
    ) : step === 1 ? (
      <>
        <Btn variant="quiet" className="btn-pill" onClick={() => setStep(0)}>
          <LuChevronLeft size={15} /> Back
        </Btn>
        <Btn full className="btn-pill btn-mint" onClick={runMerge} disabled={nothingPicked}>
          <LuFileText size={15} /> {nothingPicked ? "Choose at least one" : "Build my resume"}
        </Btn>
      </>
    ) : after ? (
      <>
        <Btn variant="quiet" className="btn-pill" onClick={() => setStep(1)}>
          <LuChevronLeft size={15} /> Change
        </Btn>
        {/* Download PDF = print the on-screen preview (instant, crisp, ATS-clean,
            matches what you see). Replaces the slow, poorly-formatted server PDF. */}
        <Btn className="btn-pill btn-mint" onClick={printResume}>
          <LuDownload size={15} /> Download PDF
        </Btn>
        <Btn
          variant="ghost"
          className="btn-pill"
          onClick={() => download("docx")}
          loading={downloading === "docx"}
          disabled={Boolean(downloading)}
        >
          <LuDownload size={15} /> Word (.docx)
        </Btn>
      </>
    ) : null;

  return (
    <Sheet open={open} onClose={onClose} title="Custom resume" width="min(1120px, calc(100vw / var(--csx-zoom, 1)))" footer={footer}>
      <ol className="cr-steps" aria-label="Progress">
        {STEPS.map((s, i) => (
          <li key={s} className={i === step ? "on" : i < step ? "done" : ""}>
            <span className="cr-step-n">{i < step ? <LuCheck size={12} /> : i + 1}</span>
            {s}
          </li>
        ))}
      </ol>

      {job ? (
        <p className="t-sm cr-job">
          {job.job_title} · {job.employer_name}
        </p>
      ) : null}

      {/* ---------- step 1: the gap ---------- */}
      {step === 0 ? (
        <div className="stack-3">
          <div className="cr-score">
            <MatchRing score={score} size={72} />
            <div className="grow">
              <div className="t-h3">
                {score >= 75
                  ? "Your resume is a strong match"
                  : score >= 50
                  ? "Your resume needs a few changes"
                  : "Your resume is a low match for this job"}
              </div>
              {assessment.verdict_reason ? <p className="t-sm mt-1">{assessment.verdict_reason}</p> : null}
            </div>
          </div>

          <div className="cr-table">
            <div className="cr-row cr-row-head">
              <div className="cr-row-label">Compared</div>
              <div className="cr-row-side">This job</div>
              <div className="cr-row-side">Your resume</div>
            </div>
            <DiffRow
              label="Job title"
              ok={
                Boolean(resumeJson?.title) &&
                String(job?.job_title || "").toLowerCase().includes(String(resumeJson.title).toLowerCase())
              }
              jobSide={job?.job_title || "—"}
              youSide={resumeJson?.title || "Not on file"}
            />
            {domain.jd_domain || domain.resume_domain ? (
              <DiffRow
                label="Domain"
                ok={/high|strong/i.test(domain.match_level || "")}
                jobSide={domain.jd_domain || "—"}
                youSide={domain.resume_domain || "—"}
              />
            ) : null}
            <DiffRow
              label={`Skills${missingSkills.length ? ` (${missingSkills.length} gap${missingSkills.length === 1 ? "" : "s"})` : ""}`}
              ok={missingSkills.length === 0}
              jobSide={
                missingSkills.length ? (
                  <ul className="cr-gaps">
                    {missingSkills.slice(0, 5).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                    {missingSkills.length > 5 ? (
                      <li className="t-xs">+{missingSkills.length - 5} more</li>
                    ) : null}
                  </ul>
                ) : (
                  "Covered"
                )
              }
              youSide={missingSkills.length ? "Not mentioned on your resume" : "All covered"}
            />
            {(certs.required || []).length || (certs.missing || []).length ? (
              <DiffRow
                label="Certifications"
                ok={!(certs.missing || []).length}
                jobSide={(certs.required || []).join(", ") || "—"}
                youSide={(certs.missing || []).length ? `Missing ${certs.missing.join(", ")}` : "All covered"}
              />
            ) : null}
            {experience.length ? (
              <DiffRow
                label="Work experience"
                ok={false}
                jobSide={`${experience.length} role${experience.length === 1 ? "" : "s"} can be reworded`}
                youSide="Bullets don't use the posting's language"
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ---------- step 2: choose ---------- */}
      {step === 1 ? (
        <div className="cr-pick">
          <div>
            <div className="cr-pick-h">What should we rewrite?</div>
            <div className="stack-2">
              {sections.map((s) => (
                <label className={`cr-opt${picked[s.key] ? " on" : ""}`} key={s.key}>
                  <input
                    type="checkbox"
                    checked={Boolean(picked[s.key])}
                    onChange={(e) => setPicked((p) => ({ ...p, [s.key]: e.target.checked }))}
                  />
                  <span className="cr-opt-body">
                    <span className="cr-opt-t">
                      {s.icon} {s.label}
                    </span>
                    <span className="cr-opt-h">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {picked.skill_gaps && missingSkills.length ? (
            <div>
              <div className="cr-pick-h">
                Requirements to address
                <button
                  type="button"
                  className="cr-linkbtn"
                  onClick={() => setSkillPicks(skillPicks.length === missingSkills.length ? [] : missingSkills)}
                >
                  {skillPicks.length === missingSkills.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <Note tone="info">
                <LuInfo size={15} />
                <span>Only tick what you can genuinely speak to in an interview.</span>
              </Note>
              <div className="cr-chips mt-2">
                {missingSkills.map((s) => {
                  const on = skillPicks.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      className={`cr-chip pick${on ? " on" : ""}`}
                      aria-pressed={on}
                      onClick={() =>
                        setSkillPicks((p) => (on ? p.filter((x) => x !== s) : [...p, s]))
                      }
                    >
                      {on ? <LuCheck size={12} /> : null} {s}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---------- step 3: review ---------- */}
      {step === 2 ? (
        merging || !after ? (
          <div className="cr-wait">
            <Spinner lg />
            <div className="t-h3 mt-2">Rewriting your resume</div>
            <p className="t-sm">This usually takes 10–20 seconds.</p>
          </div>
        ) : (
          <div className="cr-review">
            <div className="cr-doc-wrap">
              <ResumeDoc resume={after} compareTo={resumeJson} page={null} />
            </div>
            <aside className="cr-side">
              <div className="cr-changed">
                <div className="cr-pick-h">What changed</div>
                <ul className="dot-list">
                  {picked.skill_gaps && skillPicks.length ? (
                    <li>
                      <LuCheck size={14} className="t-accent" />
                      <span>
                        Addressed {skillPicks.length} requirement{skillPicks.length === 1 ? "" : "s"}
                      </span>
                    </li>
                  ) : null}
                  {picked.experience_enhancement && experience.length ? (
                    <li>
                      <LuCheck size={14} className="t-accent" />
                      <span>
                        Reworded bullets across {experience.length} role{experience.length === 1 ? "" : "s"}
                      </span>
                    </li>
                  ) : null}
                  {picked.certifications && (certs.missing || []).length ? (
                    <li>
                      <LuCheck size={14} className="t-accent" />
                      <span>
                        Flagged {certs.missing.length} certification{certs.missing.length === 1 ? "" : "s"}
                      </span>
                    </li>
                  ) : null}
                </ul>
                {resumeJson ? (
                  <p className="t-xs mt-2">Highlighted text is new or rewritten.</p>
                ) : (
                  <p className="t-xs mt-2">
                    Upload-side copy of your resume wasn't available, so changes aren't highlighted.
                  </p>
                )}
              </div>
            </aside>
          </div>
        )
      ) : null}
    </Sheet>
  );
}
